import type { ReploProject } from "schemas/generated/project";

import * as React from "react";

import { skipToken } from "@tanstack/react-query";
import { ConnectShopifyIntegrationCta } from "@v2/components/ConnectShopifyCallout";
import { useCurrentWorkspaceId } from "@v2/contexts/WorkspaceDashboardContext";
import { ShopifyIntegration } from "@v2/features/settings/shop/integrations/ShopifyIntegration";
import { StripeConnectIntegration } from "@v2/features/settings/shop/integrations/StripeConnectIntegration";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { integrationRouter } from "@v2/trpc/routes/integration";
import { stripeConnectRouter } from "@v2/trpc/routes/stripeConnect";

export const PaymentMethodSection: React.FC<{
  project: ReploProject;
}> = ({ project }) => {
  const projectId = useCurrentProjectId();
  const workspaceId = useCurrentWorkspaceId();

  const { data: onboardingStatus } =
    stripeConnectRouter.stripeConnectGetOnboardingStatus.useQuery(
      projectId ? { projectId } : skipToken,
    );

  const { data: integrations, isLoading: isLoadingIntegrations } =
    integrationRouter.find.useQuery(projectId ? { projectId } : skipToken);

  const isShopifyConnected =
    integrations?.some(
      (integration) => integration.type === "ShopifyIntegrationConfig",
    ) ?? false;

  const isStripeConnected =
    integrations?.some((integration) => integration.type === "StripeConnect") ??
    false;

  const isStripeFullyOnboarded =
    isStripeConnected && onboardingStatus?.onboardingCompleted;

  if (isLoadingIntegrations) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse">
          <div className="h-20 bg-surface rounded-xl"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {!isStripeFullyOnboarded && !isShopifyConnected && (
        <div className="space-y-3">
          <StripeConnectIntegration variant="black-box" />

          <div className="flex items-center justify-between p-4 border-0.5 border-border rounded-xl bg-white">
            <div className="flex items-center gap-3">
              <div className="p-2 border-0.5 border-border rounded-lg">
                <img
                  src="/images/integration-logos/shopify.png"
                  alt="Shopify"
                  className="w-6 h-6"
                />
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-medium">Shopify</span>
                <span className="text-xs text-muted">
                  Connect Shopify store to sync product data and manage
                  payments.
                </span>
              </div>
            </div>
            <ConnectShopifyIntegrationCta
              cookieValue={{
                type: "shopSettingsIntegration",
                projectId: projectId!,
                workspaceId: workspaceId!,
              }}
              ctaText="Set up"
              size="base"
              showEndIcon
              className="w-fit"
            />
          </div>
        </div>
      )}

      {isStripeFullyOnboarded && (
        <StripeConnectIntegration variant="white-box" />
      )}

      {isShopifyConnected && !isStripeConnected && (
        <div className="p-4 border-0.5 border-border rounded-xl">
          <ShopifyIntegration project={project} />
        </div>
      )}
    </div>
  );
};
